import React from "react"
import { usePageContext } from "vike-react/usePageContext"
import { ServeAd, TrackImpression, clickHref } from "./api"

/**
 * One-liner ad slot: <AldooAd id="<adId>" account="<accountIdOrApiUrl>"/>.
 *
 * Resolution order:
 *   1. SSR-prefetched payload from pageContext.aldooAds (see ./prefetch.js)
 *   2. client fetch through the local api's serve_proxy
 *
 * The served HTML is fully self-contained (styles inlined at save time) and
 * namespaced under .aldoo-ad-<id>, so it can't clash with host styles.
 * An impression beacon fires once per mount when the ad actually renders;
 * clicks open the origin api's redirect (counts + utm-stamps + 302 to CTA).
 * Renders nothing when the ad is stopped/unavailable.
 */
const AldooAd = ({ id, account, className, target = "_blank" }) => {
  const pageContext = usePageContext()
  const prefetchKey = `${account || "local"}:${id}`
  const prefetched = pageContext?.aldooAds?.[prefetchKey]

  const [data, setData] = React.useState(prefetched?.result ? prefetched : null)
  const tracked = React.useRef(false)

  React.useEffect(() => {
    if (data || !id) return
    let alive = true
    ;(async () => {
      const res = await ServeAd(id, account).catch(() => null)
      if (alive && res?.result) setData(res)
    })()
    return () => { alive = false }
  }, [id, account])

  // Impression fires when html is actually on screen (mount with data),
  // not at SSR prefetch — aborted renders shouldn't count.
  React.useEffect(() => {
    if (!data?.result || tracked.current) return
    tracked.current = true
    TrackImpression(data)
  }, [data])

  if (!data?.result || !data?.html) return null

  const href = clickHref(data)
  const open = () => {
    if (!href) return
    if (target === "_self") window.location.href = href
    else window.open(href, target, "noopener,noreferrer")
  }

  return (
    <div
      className={className}
      style={{ cursor: "pointer" }}
      onClick={open}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") open() }}
      dangerouslySetInnerHTML={{ __html: data.html }}
    />
  )
}

export default AldooAd
